import { useEffect } from 'react'
import { NavLink, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { getRoomMembers, getRoomDetails } from '@/redux/asyncThunks/room'
import { fetchTeamsByRoomId } from '@/redux/asyncThunks/team'
import { selectRoomPlayers } from '@/redux/selectors/roomSelector'
import OGEAvatar from '../OGE/OGEAvatar'
import { SERVER_URL } from '@/api/config'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ExternalLink, Users, ArrowLeftCircle } from 'lucide-react'
import ROUTES from '@/routes/routes'
import { getTeamNameById } from '@/utils/teamUtils'

const SimulationPlayersView = () => {
  const { roomId } = useParams<{ roomId: string }>()
  const dispatch = useAppDispatch()
  const players = useAppSelector(selectRoomPlayers)
  const { currentRoom, isLoading } = useAppSelector((state) => state.room)
  const { teams } = useAppSelector((state) => state.team)

  useEffect(() => {
    if (roomId) {
      dispatch(getRoomMembers(roomId))
      dispatch(getRoomDetails(roomId))
      dispatch(fetchTeamsByRoomId(roomId))
    }
  }, [dispatch, roomId])

  const handlePlayerClick = (playerId: string) => {
    if (!roomId) return

    const playerUrl = ROUTES.PROFESSOR.GAME(roomId, {
      playerId: playerId,
      simulation: 'true',
      inviteCode: currentRoom?.invite_code,
    })
    window.open(playerUrl, '_blank')
  }

  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-lg text-gray-600'>Loading players...</div>
      </div>
    )
  }

  return (
    <div className='min-h-screen px-24 py-5'>
      <div className='max-w-7xl mx-auto'>
        <div className='mb-6 flex items-center justify-between'>
          <div className='flex items-center gap-4'>
            {roomId && (
              <NavLink
                to={ROUTES.PROFESSOR.ROOM_DETAILS(roomId)}
                className='flex-shrink-0'
              >
                <ArrowLeftCircle className='text-dark-blue hover:text-dark-blue-hovered' />
              </NavLink>
            )}
            <div>
              <h1 className='text-2xl font-bold text-dark-blue'>
                Simulation Players
              </h1>
            </div>
          </div>
        </div>

        {players.length === 0 ? (
          <Card>
            <CardContent className='py-12 text-center'>
              <Users className='h-12 w-12 text-gray-400 mx-auto mb-4' />
              <p className='text-lg text-gray-600 mb-2'>No players found</p>
              <p className='text-sm text-gray-500'>
                Players will appear here once they join the room or when dummy
                players are created for simulation.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'>
            {players.map((player) => {
              const teamName = player.teamId
                ? getTeamNameById(teams, player.teamId)
                : null
              const isDummyPlayer =
                player.member_email?.endsWith('@simulation.local')

              return (
                <Card
                  key={player.gamealias}
                  className='hover:shadow-lg transition-all duration-200 cursor-pointer border-2 hover:border-blue-500'
                  onClick={() =>
                    player.member_id && handlePlayerClick(player.member_id)
                  }
                >
                  <CardHeader className='pb-3'>
                    <div className='flex items-center gap-3'>
                      <OGEAvatar
                        src={
                          player.member_profile_picture
                            ? SERVER_URL + player.member_profile_picture
                            : null
                        }
                        fallback={
                          player.member_name?.substring(0, 2).toUpperCase() ||
                          'P'
                        }
                        variant='lg'
                        isLoading={false}
                      />
                      <div className='flex-1 min-w-0'>
                        <h3 className='font-semibold text-gray-900 truncate'>
                          {player.member_name || player.gamealias}
                        </h3>
                        {isDummyPlayer && (
                          <span className='inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded'>
                            Dummy Player
                          </span>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className='space-y-2'>
                      <div>
                        <p className='text-xs text-gray-500 mb-1'>Email</p>
                        <p className='text-sm text-gray-700 truncate'>
                          {player.member_email}
                        </p>
                      </div>

                      {player.teamId && (
                        <div>
                          <p className='text-xs text-gray-500 mb-1'>Team</p>
                          <p className='text-sm font-medium text-blue-600'>
                            {teamName || `Team ${player.teamId}`}
                          </p>
                        </div>
                      )}

                      <div>
                        <p className='text-xs text-gray-500 mb-1'>Status</p>
                        <p className='text-sm text-gray-700 capitalize'>
                          {player.invitation_status || 'Unknown'}
                        </p>
                      </div>

                      <Button
                        className='w-full mt-4'
                        variant='outline'
                        onClick={(e) => {
                          e.stopPropagation()
                          player.member_id &&
                            handlePlayerClick(player.member_id)
                        }}
                      >
                        <ExternalLink className='h-4 w-4 mr-2' />
                        Open Player View
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}

        {currentRoom?.is_simulating && (
          <Card className='mt-6 bg-blue-50 border-blue-200'>
            <CardContent className='py-4'>
              <div className='flex items-start gap-3'>
                <div className='flex-1'>
                  <h4 className='font-semibold text-blue-900 mb-1'>
                    Simulation Mode Active
                  </h4>
                  <p className='text-sm text-blue-700'>
                    Click on any player card to open their game interface in a
                    new tab. This allows you to test the game experience from
                    different player perspectives.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}

export default SimulationPlayersView
